import { useEffect, useState, useCallback } from 'react';

/**
 * Resolves the chart palette CSS tokens (--chart-1..5, --chart-issue) plus a few
 * neutral tokens into concrete hex/rgb strings for Recharts, which cannot read
 * CSS variables from its fill/stroke props reliably.
 *
 * Re-resolves when the theme changes (data-theme on <html>) so charts stay
 * theme-aware without hardcoding colors.
 */
export interface ChartColors {
  series: [string, string, string, string, string];
  issue: string;
  grid: string;
  axis: string;
  surface: string;
  muted: string;
  green: string;
}

function readColors(): ChartColors {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => s.getPropertyValue(name).trim() || fallback;
  return {
    series: [
      v('--chart-1', '#2a78d6'),
      v('--chart-2', '#1baf7a'),
      v('--chart-3', '#eda100'),
      v('--chart-4', '#e87ba4'),
      v('--chart-5', '#008300'),
    ],
    issue: v('--chart-issue', '#e34948'),
    grid: v('--border-1', '#e5e7eb'),
    axis: v('--text-4', '#9ca3af'),
    surface: v('--surface-1', '#ffffff'),
    muted: v('--text-4', '#9ca3af'),
    green: v('--chart-5', '#008300'),
  };
}

export function useChartColors(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(readColors);
  const refresh = useCallback(() => setColors(readColors()), []);

  useEffect(() => {
    const observer = new MutationObserver(refresh);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    // Re-read once after mount in case tokens weren't ready during SSR/first paint.
    refresh();
    return () => observer.disconnect();
  }, [refresh]);

  return colors;
}
